import { AppState, type StateController } from '../state-controller';
import { Analytics } from '../analytics';
import { Logger } from '../logger';
import { Hermes } from '../network';
import { dismissStartingScreen } from './starting-screen';
import type { StandbyData, StandbyHighlight } from '../types/ui';

/**
 * Standby screen overlay — displays personalized catalog highlights while the
 * receiver is idle, before any media load.
 *
 * **Initialization flow:**
 * 1. `initStandbyScreen()` caches DOM elements and subscribes to state.
 * 2. `fetchAndPreloadHighlights()` is called early by `app.ts` (no auth needed).
 *    It fetches highlights, preloads the first background image, then resolves.
 * 3. `app.ts` transitions STARTING → IDLE only after the promise resolves,
 *    so the standby screen appears with the first image already loaded.
 * 4. When the user-channel receives a token, `setUserProfile()` adds the
 *    avatar and name on top of the already-visible highlights.
 */

const LOG_TAG = 'UI_STANDBY';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const HIDDEN_CLASS = 'screen-overlay--hidden';
const CAROUSEL_INTERVAL_MS = 15_000;

/** Delay before showing standby — lets the starting screen cross-fade begin first. */
const SHOW_DELAY_MS = 0;

// ---------------------------------------------------------------------------
// Module state
// ---------------------------------------------------------------------------

let isVisible = false;
let isDimmed = false;
let currentData: StandbyData | null = null;
let currentIndex = 0;
let carouselTimer: ReturnType<typeof setInterval> | null = null;

// ---------------------------------------------------------------------------
// Image CDN
// ---------------------------------------------------------------------------

/** Pick image width using devicePixelRatio (not affected by viewport lock).
 *  Viewport is 1920px, so dpr>=2 means a 4K physical screen. */
function getImageWidth(): number {
  const dpr = window.devicePixelRatio || 1;
  if (dpr >= 2) return 3840;
  return 1920;
}

const IMAGE_BASE_URL = `https://picturae.bp.app/image/upload/c_scale,f_auto,q_auto:low,w_${getImageWidth()}/v1/`;

/** Resolves a path against the CDN base URL. */
function resolveCdnUrl(path: string): string {
  if (path.startsWith('http://') || path.startsWith('https://')) return path;
  const cleanPath = path.startsWith('/') ? path.slice(1) : path;
  return `${IMAGE_BASE_URL}${cleanPath}`;
}

// ---------------------------------------------------------------------------
// DOM references (cached once on init)
// ---------------------------------------------------------------------------

let screenEl: HTMLElement | null = null;
let bgElA: HTMLImageElement | null = null;
let bgElB: HTMLImageElement | null = null;
let activeBg: HTMLImageElement | null = null;
let gradientEl: HTMLElement | null = null;
let logoEl: HTMLImageElement | null = null;
let titleEl: HTMLElement | null = null;
let ratingEl: HTMLImageElement | null = null;
let metaEl: HTMLElement | null = null;
let descEl: HTMLElement | null = null;
let directorsEl: HTMLElement | null = null;
let detailEl: HTMLElement | null = null;
let avatarEl: HTMLImageElement | null = null;
let userNameEl: HTMLElement | null = null;
let userContainerEl: HTMLElement | null = null;

function cacheDomElements(): boolean {
  screenEl = document.getElementById('standby-screen');
  if (!screenEl) return false;

  // Create two bg <img> elements on <body> for cross-fade transitions.
  // The Chromecast WebView does not render position:absolute images inside
  // flex containers, nor CSS background-image.
  const bgDiv = screenEl.querySelector('.standby__bg');
  if (bgDiv) bgDiv.remove();

  bgElA = document.createElement('img');
  bgElA.alt = '';
  bgElA.className = 'standby-bg';
  document.body.appendChild(bgElA);

  bgElB = document.createElement('img');
  bgElB.alt = '';
  bgElB.className = 'standby-bg';
  document.body.appendChild(bgElB);

  // Create gradient overlay on <body> — Chromecast WebView doesn't render
  // position:fixed inside another position:fixed element.
  const existingGradient = screenEl.querySelector('.standby__gradient');
  if (existingGradient) existingGradient.remove();

  gradientEl = document.createElement('div');
  gradientEl.className = 'standby__gradient';
  gradientEl.style.display = 'none';
  document.body.appendChild(gradientEl);

  activeBg = bgElA;

  logoEl = screenEl.querySelector('.standby__logo');
  titleEl = screenEl.querySelector('.standby__title');
  ratingEl = screenEl.querySelector('.standby__rating');
  metaEl = screenEl.querySelector('.standby__meta');
  descEl = screenEl.querySelector('.standby__description');
  directorsEl = screenEl.querySelector('.standby__directors');
  detailEl = screenEl.querySelector('.standby__detail');

  // Replace div.standby__avatar with <img>
  const avatarDiv = screenEl.querySelector('.standby__avatar');
  if (avatarDiv) {
    avatarEl = document.createElement('img');
    avatarEl.className = avatarDiv.className;
    avatarEl.alt = '';
    avatarDiv.replaceWith(avatarEl);
  }

  userNameEl = screenEl.querySelector('.standby__user-name');
  userContainerEl = screenEl.querySelector('.standby__user');

  return true;
}

// ---------------------------------------------------------------------------
// Show / Hide
// ---------------------------------------------------------------------------

let castPlayerEl: HTMLElement | null = null;

function showScreen(): void {
  if (!screenEl || !activeBg) return;

  // Hide cast-media-player immediately (no fade — cut to standby)
  if (!castPlayerEl) {
    castPlayerEl = document.querySelector('cast-media-player') as HTMLElement | null;
  }
  if (castPlayerEl) {
    castPlayerEl.style.transition = 'none';
    castPlayerEl.style.visibility = 'hidden';
  }

  // Show the screen overlay (panel content)
  screenEl.classList.remove(HIDDEN_CLASS);
  screenEl.style.display = '';

  // Show gradient overlay on body
  if (gradientEl) gradientEl.style.display = 'block';

  // Load first highlight
  if (currentData && currentData.highlights.length > 0) {
    const highlight = currentData.highlights[currentIndex];
    if (highlight) {
      activeBg.src = resolveCdnUrl(highlight.backgroundImageUrl);
      renderHighlightContent(highlight);
    }
  }

  // Show active bg with Ken Burns
  activeBg.style.display = 'block';
  activeBg.classList.add('standby-bg--active');
  requestAnimationFrame(() => {
    if (activeBg) activeBg.style.opacity = '1';
  });

  isVisible = true;

  startCarousel();

  // Dismiss starting screen now that standby content is ready
  dismissStartingScreen();

  Analytics.logEvent(Analytics.eventNames.CAST_STANDBY_DISPLAYED);
  Logger.info(LOG_TAG, 'Standby screen shown');
}

function resetBgEl(el: HTMLImageElement | null): void {
  if (!el) return;
  el.style.display = 'none';
  el.style.opacity = '0';
  el.classList.remove('standby-bg--active');
}

function hideScreen(): void {
  if (!screenEl) return;

  screenEl.classList.add(HIDDEN_CLASS);
  resetBgEl(bgElA);
  resetBgEl(bgElB);
  if (gradientEl) gradientEl.style.display = 'none';
  activeBg = bgElA;
  isVisible = false;

  // Reveal cast-media-player for media playback (visible under the fading overlay)
  if (!castPlayerEl) {
    castPlayerEl = document.querySelector('cast-media-player') as HTMLElement | null;
  }
  if (castPlayerEl) {
    castPlayerEl.style.transition = '';
    castPlayerEl.style.visibility = 'visible';
  }

  stopCarousel();

}

function setDimmed(dim: boolean): void {
  if (!screenEl || isDimmed === dim) return;

  isDimmed = dim;
  screenEl.style.opacity = dim ? '0.15' : '1';
  if (activeBg) activeBg.style.opacity = dim ? '0.15' : '1';

}

// ---------------------------------------------------------------------------
// Rendering
// ---------------------------------------------------------------------------

function renderUserProfile(): void {
  if (!currentData?.user) {
    if (userContainerEl) {
      userContainerEl.classList.remove('standby__user--visible');
    }
    return;
  }

  const { name, avatarUrl } = currentData.user;

  if (avatarEl && avatarUrl) {
    avatarEl.src = resolveCdnUrl(avatarUrl);
  }

  if (userNameEl) {
    userNameEl.textContent = name;
  }

  if (userContainerEl) {
    userContainerEl.classList.add('standby__user--visible');
  }
}

/** Updates highlight text/logo content. */
function renderHighlightContent(highlight: StandbyHighlight): void {
  // Logo — when absent, hide both logo and title (no plain-text fallback)
  if (logoEl && titleEl) {
    if (highlight.logoUrl) {
      logoEl.src = resolveCdnUrl(highlight.logoUrl);
      logoEl.alt = '';
      logoEl.classList.remove('standby__logo--hidden');
      titleEl.classList.add('standby__title--hidden');
    } else {
      logoEl.classList.add('standby__logo--hidden');
      titleEl.classList.add('standby__title--hidden');
      titleEl.textContent = '';
    }
  }

  // Rating image
  if (ratingEl) {
    if (highlight.ratingImageUrl) {
      ratingEl.src = resolveCdnUrl(highlight.ratingImageUrl);
      ratingEl.classList.remove('standby__rating--hidden');
    } else {
      ratingEl.classList.add('standby__rating--hidden');
      ratingEl.removeAttribute('src');
    }
  }

  if (metaEl) {
    metaEl.textContent = highlight.details;
  }

  if (descEl) {
    descEl.textContent = highlight.description;
  }

  if (directorsEl) {
    if (highlight.directors.length > 0) {
      const label = highlight.directors.length === 1 ? 'Diretor' : 'Diretores';
      directorsEl.innerHTML = `<span class="standby__directors-label">${label}</span>${highlight.directors.join(', ')}`;
    } else {
      directorsEl.textContent = '';
    }
  }
}

// ---------------------------------------------------------------------------
// Carousel
// ---------------------------------------------------------------------------

/** Transition durations (ms) — must match CSS transitions */
const BG_FADE_OUT_MS = 300;
const CONTENT_EXIT_MS = 800;
const CONTENT_ENTER_MS = 300;
const BG_FADE_IN_MS = 800;

function getInactiveBg(): HTMLImageElement | null {
  return activeBg === bgElA ? bgElB : bgElA;
}

/**
 * Sequential carousel transition:
 * 1. BG fade out (300ms)
 * 2. Content slide-out left + fade out (800ms)
 * 3. Content slide-in from left + fade in (300ms)
 * 4. BG fade in (800ms)
 */
function advanceCarousel(): void {
  if (!currentData || currentData.highlights.length <= 1) return;

  currentIndex = (currentIndex + 1) % currentData.highlights.length;
  const highlight = currentData.highlights[currentIndex];
  if (!highlight) return;

  const incoming = getInactiveBg();
  const outgoing = activeBg;
  if (!incoming || !outgoing) return;

  // Step 1: BG fade out
  outgoing.style.transition = `opacity ${BG_FADE_OUT_MS}ms ease-in-out`;
  outgoing.style.opacity = '0';

  // Step 2: After BG fades out, content exits
  setTimeout(() => {
    outgoing.style.display = 'none';
    outgoing.classList.remove('standby-bg--active');

    if (detailEl) {
      detailEl.classList.add('standby__detail--exit');
    }

    // Step 3: After content exits, update content and slide in
    setTimeout(() => {
      renderHighlightContent(highlight);

      if (detailEl) {
        detailEl.classList.remove('standby__detail--exit');
        detailEl.classList.add('standby__detail--before-enter');
        void detailEl.offsetHeight;
        detailEl.classList.remove('standby__detail--before-enter');
        detailEl.classList.add('standby__detail--enter');
      }

      // Step 4: After content enters, fade in new BG
      setTimeout(() => {
        incoming.src = resolveCdnUrl(highlight.backgroundImageUrl);
        incoming.style.display = 'block';
        incoming.style.opacity = '0';
        incoming.style.transition = `opacity ${BG_FADE_IN_MS}ms ease-in-out`;
        incoming.classList.remove('standby-bg--active');

        void incoming.offsetHeight;

        incoming.classList.add('standby-bg--active');
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            incoming.style.opacity = '1';
          });
        });

        activeBg = incoming;

        // Clean up detail classes after BG fade in
        setTimeout(() => {
          detailEl?.classList.remove('standby__detail--enter');
        }, BG_FADE_IN_MS);
      }, CONTENT_ENTER_MS);
    }, CONTENT_EXIT_MS);
  }, BG_FADE_OUT_MS);

}

function startCarousel(): void {
  stopCarousel();

  if (!currentData || currentData.highlights.length <= 1) return;

  carouselTimer = setInterval(advanceCarousel, CAROUSEL_INTERVAL_MS);
}

function stopCarousel(): void {
  if (carouselTimer !== null) {
    clearInterval(carouselTimer);
    carouselTimer = null;
  }
}

// ---------------------------------------------------------------------------
// Image preloading
// ---------------------------------------------------------------------------

/** Preloads an image URL — resolves when loaded, resolves on error too (best-effort). */
function preloadImage(url: string): Promise<void> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve();
    img.onerror = () => resolve();
    img.src = url;
  });
}

/** Preloads logo (if present) then background for a single highlight. */
async function preloadHighlightImages(h: StandbyHighlight): Promise<void> {
  if (h.logoUrl) {
    await preloadImage(resolveCdnUrl(h.logoUrl));
  }
  await preloadImage(resolveCdnUrl(h.backgroundImageUrl));
}

/** Preloads remaining highlights sequentially in the background. */
function preloadRemainingHighlights(items: StandbyHighlight[]): void {
  let i = 0;
  const next = (): void => {
    if (i >= items.length) return;
    preloadHighlightImages(items[i]!).then(() => {
      i++;
      next();
    });
  };
  next();
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Fetches standby highlights from Hermes (no auth needed) and preloads the
 * first background image. Resolves once the image is ready to display.
 *
 * Called early by `app.ts` during boot — the starting screen stays visible
 * until this promise resolves.
 */
export async function fetchAndPreloadHighlights(): Promise<void> {
  const highlights: StandbyHighlight[] = [];

  try {
    const result = await Hermes.client.GetCastStandbyPage();
    const page = result.mobile_app_cast_standby_page;

    if ('error_type' in page) {
      Logger.error(LOG_TAG, `Standby page error: [${page.error_type}] ${page.message}`);
    } else {
      for (const item of page.items) {
        highlights.push({
          description: item.description,
          backgroundImageUrl: item.background_image,
          logoUrl: item.logo || null,
          directors: item.directors ?? [],
          details: item.details ?? '',
          ratingImageUrl: item.rating_image || null,
        });
      }
    }

  } catch (err: unknown) {
    Logger.error(LOG_TAG, 'Failed to fetch standby highlights', err);
  }

  // Store data (user will be added later by setUserProfile)
  currentData = {
    user: null,
    highlights,
  };

  // Preload all highlight images sequentially: logo then background for each.
  // The first item must complete before STARTING → IDLE transition;
  // remaining items continue loading in the background.
  if (highlights.length > 0) {
    await preloadHighlightImages(highlights[0]!);

    // Pre-set the first image on the real DOM element so it's already decoded
    // when showScreen() runs — avoids a re-download/decode delay on Chromecast.
    if (activeBg) {
      activeBg.src = resolveCdnUrl(highlights[0]!.backgroundImageUrl);
    }
    if (logoEl && highlights[0]!.logoUrl) {
      logoEl.src = resolveCdnUrl(highlights[0]!.logoUrl);
    }

    // Continue preloading remaining items in background (don't block boot)
    preloadRemainingHighlights(highlights.slice(1));
  }
}

/**
 * Initialises the standby screen and wires it to the application state.
 */
export function initStandbyScreen(stateController: StateController): void {
  if (!cacheDomElements()) return;

  stateController.subscribe((state: AppState) => {
    switch (state) {
      case AppState.IDLE:
        setDimmed(false);
        // Delay so the starting screen fully fades out before standby fades in
        setTimeout(() => showScreen(), SHOW_DELAY_MS);
        break;

      case AppState.STANDBY:
        if (!isVisible) showScreen();
        setDimmed(true);
        break;

      default:
        if (isVisible) {
          hideScreen();
          setDimmed(false);
        }
        break;
    }
  });
}

/**
 * Updates the user profile on the standby screen (avatar + name).
 * Called by `app.ts` after user-channel receives the token and fetches
 * the user profile. Highlights are already loaded at this point.
 */
export function setUserProfile(user: {
  userId: string;
  name: string;
  avatarUrl: string;
}): void {
  if (!currentData) return;

  currentData.user = user;

  renderUserProfile();
}
